import { useState } from 'react';

import { updateSubmission } from '../api/client.js';
import { useAuth } from '../context/AuthContext.jsx';
import ActivityLog from './ActivityLog.jsx';
import AssigneeDropdown from './AssigneeDropdown.jsx';
import SentimentBadge from './SentimentBadge.jsx';
import SeverityBadge from './SeverityBadge.jsx';
import StatusBadge from './StatusBadge.jsx';
import TagChip from './TagChip.jsx';

const STATUS_OPTIONS = [
  { value: 'unreviewed', label: 'Unreviewed' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'escalated', label: 'Escalated' },
  { value: 'pending_complete', label: 'Pending Complete' },
  { value: 'resolved', label: 'Resolved' },
  { value: '__divider__', label: '──────────', disabled: true },
  { value: 'no_action_needed', label: 'No Action Needed' },
];

const SEVERITY_OPTIONS = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function EaseScore({ value }) {
  if (!value) return <span>—</span>;
  const map = { 'Very Easy': 5, Easy: 4, Neutral: 3, Difficult: 2, 'Very Difficult': 1 };
  const stars = map[value];
  if (!stars) return <span>{value}</span>;
  return (
    <span className="ease-meter">
      <span aria-hidden="true">{'★'.repeat(stars)}{'☆'.repeat(5 - stars)}</span>
      <span>{value}</span>
    </span>
  );
}

function Field({ label, value }) {
  return (
    <div className="detail-block">
      <div className="detail-block__label">{label}</div>
      <div className="detail-block__value">{value || '—'}</div>
    </div>
  );
}

export default function SubmissionDetail({ submission, onChange, onResolvedTransition }) {
  const { isAdmin } = useAuth();
  const admin = isAdmin();

  const [status, setStatus] = useState(submission.status || 'new');
  const [severity, setSeverity] = useState(submission.severity || '');
  const [notes, setNotes] = useState(submission.notes || '');
  const [assignee, setAssignee] = useState(submission.assigned_to_name || null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [assigneeError, setAssigneeError] = useState(null);
  // Bumped after any successful PATCH so the ActivityLog refetches.
  const [activityKey, setActivityKey] = useState(0);

  async function handleAssigneeChange(next) {
    setAssigneeError(null);
    const prev = assignee;
    setAssignee(next);  // optimistic
    try {
      const updated = await updateSubmission(submission.id, {
        assigned_to_name: next,
      });
      onChange?.(updated);
      setActivityKey((k) => k + 1);
    } catch (e) {
      setAssignee(prev);  // rollback
      setAssigneeError(e.message || 'Assignment failed');
    }
  }

  async function handleSave() {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const wasResolved = submission.status === 'resolved';
      const updated = await updateSubmission(submission.id, {
        status,
        severity: severity || null,
        notes,
      });
      onChange?.(updated);
      setSuccess('Saved.');
      setActivityKey((k) => k + 1);
      if (!wasResolved && status === 'resolved') {
        onResolvedTransition?.();
      }
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="detail-grid">
      <div>
        <section className="card detail-section">
          <div className="card__header">
            <h2 style={{ fontSize: 16 }}>
              {submission.full_name || 'Anonymous submission'}
            </h2>
            <div className="badges-row">
              <SeverityBadge value={submission.severity} />
              <SentimentBadge value={submission.sentiment} />
            </div>
          </div>
          <div className="card__body">
            <div className="detail-grid__contact">
              <Field label="Email" value={submission.email} />
              <Field label="Phone" value={submission.phone} />
              <Field label="Preferred Contact" value={submission.preferred_contact} />
              <Field label="Received" value={formatDate(submission.received_at)} />
              <Field label="Submitted" value={formatDate(submission.created_at)} />
              <Field label="Page Source" value={submission.page_source} />
            </div>
          </div>
        </section>

        <section className="card detail-section">
          <div className="card__body">
            <h3>Problem Summary</h3>
            <div className="detail-text" style={{ fontSize: 16, fontWeight: 600 }}>
              {submission.problem_summary || '—'}
            </div>
            <h3 style={{ marginTop: 16 }}>Reason for Visit</h3>
            <div className="detail-text" style={{ color: 'var(--color-text-muted)' }}>
              {submission.intent || '—'}
            </div>
          </div>
        </section>

        <section className="card detail-section">
          <div className="card__body">
            <h3>Ease of Experience</h3>
            <div style={{ marginBottom: 12 }}>
              <EaseScore value={submission.ease_score} />
            </div>
            <h3>Ease Detail</h3>
            <div className="detail-text">{submission.ease_detail || '—'}</div>
          </div>
        </section>

        <section className="card detail-section">
          <div className="card__body">
            <h3>Goal Outcome</h3>
            <p style={{ marginTop: 0 }}>
              <strong>Accomplished:</strong> {submission.accomplished_goal || '—'}
            </p>
            <p>
              <strong>Follow-up requested:</strong>{' '}
              {submission.follow_up_requested ? 'Yes' : 'No'}
            </p>
            <h3 style={{ marginTop: 16 }}>Experience Detail</h3>
            <div className="detail-text">{submission.experience_detail || '—'}</div>
          </div>
        </section>

        {submission.tags && submission.tags.length > 0 && (
          <section className="card detail-section">
            <div className="card__body">
              <h3>Tags</h3>
              <div>
                {submission.tags.map((t) => (
                  <TagChip key={t} label={t} />
                ))}
              </div>
            </div>
          </section>
        )}
      </div>

      <aside className="admin-panel">
        <div className="card">
          <div className="card__header">
            <h2 className="admin-panel__heading">
              {admin ? 'Admin Panel' : 'Triage Details'}
            </h2>
          </div>
          <div className="card__body">
            <div className="field">
              <label>Assigned To</label>
              <AssigneeDropdown value={assignee} onChange={handleAssigneeChange} />
              {assigneeError && (
                <div className="form-error" style={{ marginTop: 8 }}>
                  {assigneeError}
                </div>
              )}
            </div>

            <div className="field">
              <label>Status</label>
              {admin ? (
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  disabled={saving}
                >
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value} disabled={o.disabled}>
                      {o.label}
                    </option>
                  ))}
                </select>
              ) : (
                <StatusBadge value={status} />
              )}
            </div>

            <div className="field">
              <label>Severity</label>
              {admin ? (
                <select
                  value={severity || ''}
                  onChange={(e) => setSeverity(e.target.value)}
                  disabled={saving}
                >
                  <option value="">(keep current)</option>
                  {SEVERITY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              ) : (
                <SeverityBadge value={submission.severity} />
              )}
            </div>

            <div className="field">
              <label>Notes</label>
              {admin ? (
                <textarea
                  rows={5}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  disabled={saving}
                  placeholder="Internal notes for the XM team…"
                />
              ) : (
                <div className="detail-text">{submission.notes || '— (none)'}</div>
              )}
            </div>

            {error && <div className="form-error">{error}</div>}
            {success && <div className="form-success">{success}</div>}

            {admin && (
              <button
                className="btn btn--primary btn--block"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            )}
          </div>
        </div>

        <div className="card" style={{ marginTop: 16 }}>
          <div className="card__header">
            <h2 className="admin-panel__heading">Activity Log</h2>
          </div>
          <div className="card__body">
            <ActivityLog
              submissionId={submission.id}
              refreshKey={activityKey}
            />
          </div>
        </div>
      </aside>
    </div>
  );
}
